import { type PhilatelicItem, PhilatelicItemType } from '@briefmarken_katalog/types';
import { getAllItems } from '$lib/services/philatelic_items';
import { listUpdateTriggered } from '$lib/stores/trigger_list_update';
import { doLogin, user } from '$lib/stores/auth';
import { type SearchFilter, defaultSearchFilter, applyFilter } from './SearchFilter';
import { translate, locale } from '$lib/i18n';
import { get } from 'svelte/store';

const TYPE_KEYS: Record<PhilatelicItemType, string> = {
  [PhilatelicItemType.SingleStamp]:  'search.singleStamp',
  [PhilatelicItemType.Set]:          'menu.set',
  [PhilatelicItemType.StampFromSet]: 'search.stampFromSet',
  [PhilatelicItemType.Block]:        'menu.block',
  [PhilatelicItemType.Booklet]:      'menu.booklet',
  [PhilatelicItemType.SeTenants]:    'menu.seTenants',
};

export class CatalogViewModel {
  items = $state<PhilatelicItem[] | null>(null);
  error = $state<string | null>(null);
  loading = $state(true);
  itemFilter = $state<SearchFilter>(defaultSearchFilter());

  readonly visibleItems = $derived.by(() => {
    const result = applyFilter(this.items ?? [], this.itemFilter, null);
    return this.items === null ? null : result;
  });

  readonly filterSummary = $derived.by(() => {
    if (!this.itemFilter.enabled) return null;
    const parts: string[] = [];

    const { dateFrom, dateTo, descriptionWords, itemTypes } = this.itemFilter;
    const fmt = new Intl.DateTimeFormat(get(locale), { day: '2-digit', month: '2-digit', year: 'numeric' });
    const fmtDate = (iso: string) => fmt.format(new Date(iso + 'T00:00:00'));
    const from = dateFrom ? fmtDate(dateFrom) : '';
    const to   = dateTo   ? fmtDate(dateTo)   : '';
    if (from && to)   parts.push(`${from} – ${to}`);
    else if (from)    parts.push(`${translate('search.dateFrom_short')} ${from}`);
    else if (to)      parts.push(`${translate('search.dateTo_short')} ${to}`);

    if (descriptionWords.trim()) {
      const words = descriptionWords.trim();
      parts.push(words.length > 20 ? words.slice(0, 19) + '…' : words);
    }

    if (itemTypes.length === 1) {
      parts.push(translate(TYPE_KEYS[itemTypes[0]] as any));
    } else if (itemTypes.length === 2) {
      parts.push(itemTypes.map(t => translate(TYPE_KEYS[t] as any)).join(', '));
    } else if (itemTypes.length > 2) {
      parts.push(translate('search.nTypes').replace('{n}', String(itemTypes.length)));
    }

    return parts.length > 0 ? parts.join(' · ') : translate('search.enabled');
  });

  resetItemFilter() {
    this.itemFilter = defaultSearchFilter();
  }

  constructor() {
    $effect(() => {
      return listUpdateTriggered.subscribe(() => {
        this.fetchData();
      });
    });
    // The catalog can be mounted before login (mobile devices are locked to catalog
    // mode). The initial unauthenticated fetch fails, so re-fetch once a user logs in.
    let lastToken = get(user)?.token;
    $effect(() => {
      return user.subscribe((u) => {
        if (u?.token && u.token !== lastToken) {
          lastToken = u.token;
          this.fetchData();
        } else if (!u) {
          lastToken = undefined;
        }
      });
    });
  }

  private fetchGeneration = 0;

  private async fetchData() {
    const gen = ++this.fetchGeneration;
    this.loading = true;
    try {
      const items = await getAllItems(doLogin);
      if (gen !== this.fetchGeneration) return;
      this.items = items;
    } catch (err: any) {
      if (gen !== this.fetchGeneration) return;
      this.error = err instanceof Error ? err.message : 'Unknown error';
    } finally {
      if (gen === this.fetchGeneration) this.loading = false;
    }
  }
}
